import {
  Button,
  Empty,
  Popconfirm,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import axios from "axios";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { serverUrl } from "../../../../../nestserver";
import AddEditAlarmModal, { WEEKDAYS } from "./AddEditAlarmModal";

const { Text } = Typography;

const authHeaders = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

const errMsg = (err, fallback) =>
  err?.response?.data?.error?.errors?.[0] || fallback;

const describeRecurrence = (recurrence) => {
  if (!recurrence) return "—";
  if (recurrence.type === "daily") return "Every day";
  if (recurrence.type === "weekdays") return "Mon–Fri";
  const days = (recurrence.days_of_week || [])
    .slice()
    .sort((a, b) => a - b)
    .map((d) => WEEKDAYS.find((w) => w.value === d)?.label)
    .filter(Boolean);
  return days.length ? days.join(", ") : "No days selected";
};

const CLAMP_2_LINES = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const ELLIPSIS = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const ManageAlarm = () => {
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchClasses = async () => {
    const res = await axios.get(`${serverUrl}/classes`, {
      headers: authHeaders(),
    });
    return res.data;
  };
  const classes = useQuery("classes", fetchClasses);

  const fetchAlarms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${serverUrl}/wakeup-alarm`, {
        headers: authHeaders(),
      });
      setAlarms(res.data?.result || []);
    } catch (err) {
      message.error(errMsg(err, "Could not load wake-up alarms"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlarms();
  }, [fetchAlarms]);

  const handleToggle = async (record, nextActive) => {
    setAlarms((prev) =>
      prev.map((a) =>
        a._id === record._id ? { ...a, is_active: nextActive } : a,
      ),
    );
    try {
      await axios.patch(
        `${serverUrl}/wakeup-alarm/${record._id}/status`,
        { is_active: nextActive },
        { headers: authHeaders() },
      );
      message.success(nextActive ? "Alarm switched on" : "Alarm switched off");
    } catch (err) {
      setAlarms((prev) =>
        prev.map((a) =>
          a._id === record._id ? { ...a, is_active: !nextActive } : a,
        ),
      );
      message.error(errMsg(err, "Could not change the alarm status"));
    }
  };

  const handleDelete = async (record) => {
    try {
      await axios.delete(`${serverUrl}/wakeup-alarm/${record._id}`, {
        headers: authHeaders(),
      });
      message.success("Alarm deleted");
      fetchAlarms();
    } catch (err) {
      message.error(errMsg(err, "Could not delete the alarm"));
    }
  };

  const columns = [
    {
      title: "Time",
      key: "time",
      width: 150,
      render: (_, record) => (
        <div>
          <div className="text-lg font-semibold">
            {record.recurrence?.at_time
              ? moment(record.recurrence.at_time, "HH:mm").format("h:mm A")
              : "—"}
          </div>
          <Text type="secondary" className="text-xs block leading-snug">
            {describeRecurrence(record.recurrence)}
          </Text>
          <Text
            type="secondary"
            className="text-xs block mt-0.5"
            style={ELLIPSIS}
            title={record.timezone || "Asia/Kolkata"}
          >
            {record.timezone || "Asia/Kolkata"}
          </Text>
        </div>
      ),
    },
    {
      title: "Applies to",
      key: "scope",
      width: 150,
      render: (_, record) =>
        record.scope === "school" ? (
          <Tag color="blue">Whole school</Tag>
        ) : (
          <Space size={[0, 4]} wrap>
            {(record.class_ids || []).map((c) => (
              <Tag key={c?._id || c}>{c?.name || "Class"}</Tag>
            ))}
          </Space>
        ),
    },
    {
      title: "Message",
      key: "message",
      width: 280,
      render: (_, record) => (
        <div>
          <div className="font-medium" style={ELLIPSIS} title={record.title}>
            {record.title}
          </div>
          <Text
            type="secondary"
            className="text-xs block mt-0.5 leading-snug"
            style={CLAMP_2_LINES}
            title={record.message}
          >
            {record.message}
          </Text>
        </div>
      ),
    },
    {
      title: "Tone",
      key: "audio",
      width: 230,
      render: (_, record) => {
        const active = record.audio?.url ? record.audio : record.voice;
        const isRecording = !record.audio?.url && !!record.voice?.url;

        if (!active?.url) {
          return (
            <Text type="secondary" className="text-xs">
              Phone default
            </Text>
          );
        }

        return (
          <div>
            <div
              className="text-xs text-gray-500 mb-1"
              style={ELLIPSIS}
              title={active.name}
            >
              {isRecording ? "🎙 " : ""}
              {active.name}
            </div>
            <audio
              controls
              preload="none"
              src={active.url}
              style={{ width: "100%", height: 32, display: "block" }}
            />
            {record.audio?.url && record.voice?.url && (
              <Text
                type="secondary"
                className="text-xs block mt-1 leading-snug"
              >
                A recording is also saved but not used
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: "Sent to",
      key: "roles",
      width: 120,
      render: (_, record) => (
        <Space size={[0, 4]} wrap>
          {(record.target_roles || []).map((r) => (
            <Tag key={r} color={r === "parent" ? "purple" : "green"}>
              {r}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "On",
      key: "is_active",
      width: 110,
      render: (_, record) => (
        <div>
          <Switch
            checked={record.is_active}
            onChange={(checked) => handleToggle(record, checked)}
          />
          {!record.is_active && (
            <Text type="secondary" className="text-xs block mt-1">
              Nobody is woken
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 130,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            onClick={() => {
              setEditing(record);
              setModalVisible(true);
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this alarm?"
            description="Devices stop ringing it after their next sync."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div level={4} className="text-2xl pb-4 text-orange-500 font-semibold">
            Morning wake-up alarm
          </div>
        </div>
        <Button
          type="primary"
          onClick={() => {
            setEditing(null);
            setModalVisible(true);
          }}
        >
          New alarm
        </Button>
      </div>

      <div className="bg-white rounded-lg p-4">
        {loading ? (
          <div className="flex justify-center py-16">
            <Spin />
          </div>
        ) : alarms.length === 0 ? (
          <Empty
            description="No wake-up alarms yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              type="primary"
              onClick={() => {
                setEditing(null);
                setModalVisible(true);
              }}
            >
              Create the first one
            </Button>
          </Empty>
        ) : (
          <Table
            rowKey="_id"
            dataSource={alarms}
            columns={columns}
            pagination={false}
            size="middle"
            tableLayout="fixed"
            scroll={{ x: 1170 }}
          />
        )}
      </div>

      <AddEditAlarmModal
        visible={modalVisible}
        editing={editing}
        classes={classes.data}
        onClose={() => {
          setModalVisible(false);
          setEditing(null);
        }}
        onSaved={fetchAlarms}
      />
    </div>
  );
};

export default ManageAlarm;
